// /pam/policies/:product/:policyId: read-only detail. The header has the
// name, its action and whether it is on, the switch, Duplicate, Edit and
// Delete. Then the policy as the editor's sentence with four tiles (Who,
// Where, Only when, Then) and its other settings; beside it, where it sits in
// the order a sign-in is checked, and its details. After a save, the editor's
// message is a toast and a server warning (an overlap) stays as an info note.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Modal, Switch, message } from "antd";
import { Copy } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AN_COLORS, AN_MONO } from "../../design-system/tokens";
import { PageShell, RowActionMenu, fmtDate } from "../../design-system/tableUi";
import { groupKeyOf } from "../../components/policyShell/policyModel";
import { POLICY_GROUPS } from "../../components/policyShell/signals";
import { ASIDE_WIDTH, ActionPill, Eyebrow, Note, PAGE_MAX, PageTitleBar, Panel, StatusDot, TWO_COLUMNS, TextButton, actionMeta, useWide } from "./ui";
import { draftFromRow } from "./editor/draft";
import { policySummarySentence, summaryRows } from "./editor/PolicySummary";
import { PageMessage, SummarySentence } from "./PolicyEditorPage";
import { changedAt, precedenceText } from "./PoliciesListPage";
import { ProductGate, useAdapterData, usePolicyActions, usePolicyRow } from "./usePolicyPage";
import { policiesPath } from "./policyProducts";

const STAGE_FLOW = { block: "block", mfa_required: "mfa_required", allow: "allow", fallback: "allow" };

/** One of the four tiles: a label and its rows ("Groups: Finance"). */
function Tile({ label, rows, highlight }) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        padding: "0.875rem 1rem",
        borderRadius: "0.625rem",
        border: `1px solid ${highlight ? highlight.bd : AN_COLORS.line}`,
        background: highlight ? highlight.bg : AN_COLORS.bg,
        minWidth: 0,
      }}
    >
      <Eyebrow style={{ marginBottom: "0.5rem", color: highlight ? highlight.fg : AN_COLORS.muted }}>{label}</Eyebrow>
      <div style={{ display: "grid", gap: "0.375rem", fontSize: "0.875rem", color: AN_COLORS.ink2, lineHeight: 1.5, overflowWrap: "anywhere" }}>
        {rows.map((r) => (
          <div key={r.key}>
            {r.showLabel && <span style={{ color: AN_COLORS.muted }}>{r.label}: </span>}
            {r.value}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Label left, value right: the Details card and the other settings. */
function DefList({ rows }) {
  return (
    <dl style={{ margin: 0, display: "grid", gap: "0.625rem" }}>
      {rows.map((r) => (
        <div key={r.key} style={{ display: "grid", gridTemplateColumns: "7rem minmax(0, 1fr)", gap: "0.75rem", fontSize: "0.8125rem", lineHeight: 1.5 }}>
          <dt style={{ color: AN_COLORS.muted, margin: 0 }}>{r.label}</dt>
          <dd style={{ color: AN_COLORS.ink2, margin: 0, minWidth: 0, overflowWrap: "anywhere" }}>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const noop = () => {};

export default function PolicyDetailPage() {
  const { policyId } = useParams();
  return <ProductGate render={(p) => <PolicyDetail key={`${p.product}:${policyId}`} {...p} policyId={policyId} />} />;
}

function PolicyDetail({ product, adapter, flags, policyId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [messageApi, messageContext] = message.useMessage();
  const [modalApi, modalContext] = Modal.useModal();
  const data = useAdapterData(adapter, null, noop);
  const { row, loading, error, reload } = usePolicyRow(adapter, policyId, location.state?.policy);

  // "Policy created" from the editor, once. Its note (a server warning) stays.
  const toasted = useRef(false);
  useEffect(() => {
    if (toasted.current) return;
    toasted.current = true;
    if (location.state?.toast) messageApi.success(location.state.toast);
  }, [location.state, messageApi]);
  const note = location.state?.note || "";

  const { setActive, remove } = usePolicyActions(adapter, {
    messageApi,
    modalApi,
    after: (kind) => (kind === "deleted" ? navigate(policiesPath(product), { replace: true }) : reload()),
  });

  const draft = useMemo(() => (row ? draftFromRow(adapter, row) : null), [adapter, row]);
  const [testOpen, setTestOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wide = useWide(TWO_COLUMNS);

  if (!row) {
    if (loading) return <PageMessage product={product}>{"Loading policy…"}</PageMessage>;
    return <PageMessage product={product}>{error === "notFound" ? "This policy doesn't exist any more." : "Couldn't load this policy. Try again."}</PageMessage>;
  }

  const pj = row.policyJson || {};
  const labels = adapter.labels || {};
  const active = adapter.isActive(row);
  const rawId = adapter.rowId(row);
  const id = encodeURIComponent(rawId);
  const fallback = !!(adapter.features?.fallback && draft.fallback);
  const stage = groupKeyOf({ flow: draft.action, fallback });
  const meta = actionMeta(draft.action);
  const TestSignIn = adapter.features?.TestSignIn;
  const toList = () => navigate(policiesPath(product));

  // summaryRows -> the four tiles and the other settings.
  const rows = summaryRows(adapter, draft, data);
  const byKey = (k) => rows.find((r) => r.key === k);
  const whereRows = rows.filter((r) => r.section === "where" || (!r.section && !["who", "conditions", "action", "approval", "session"].includes(r.key)));
  const otherRows = rows.filter((r) => r.section === "action" || r.key === "approval" || r.key === "session");
  const tiles = [
    { label: "Who", rows: [{ ...byKey("who") }] },
    { label: "Where", rows: whereRows.length ? whereRows.map((r) => ({ ...r, showLabel: true })) : [{ key: "any", value: "Anywhere in scope" }] },
    { label: "Only when", rows: [{ ...byKey("conditions") }] },
    { label: "Then", rows: [{ ...byKey("action") }], highlight: meta },
  ];
  const changed = changedAt(row);
  const created = row.createdAt || row.created_at || row.CreatedAt || null;
  const details = [
    { key: "id", label: "Policy ID", value: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", maxWidth: "100%" }}>
        <span style={{ fontFamily: AN_MONO, fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(rawId)}>
          {String(rawId)}
        </span>
        <button
          type="button"
          aria-label="Copy policy ID"
          onClick={() => {
            if (navigator.clipboard) navigator.clipboard.writeText(String(rawId)).catch(() => {});
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: copied ? AN_COLORS.success : AN_COLORS.muted, display: "inline-flex" }}
        >
          <Copy size={13} />
        </button>
      </span>
    ) },
    row.version != null && { key: "version", label: "Version", value: String(row.version) },
    created && { key: "created", label: "Created", value: fmtDate(created) },
    changed && changed !== created && { key: "changed", label: "Last changed", value: fmtDate(changed) },
    flags.appliedColumn && row.appliedLast7d != null && {
      key: "applied",
      label: "Applied",
      value: `${row.appliedLast7d} ${row.appliedLast7d === 1 ? "time" : "times"} in the last 7 days`,
    },
  ].filter(Boolean);

  const order = (
    <Panel title="Where it sits">
      <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "0.375rem" }}>
        {POLICY_GROUPS.filter((g) => adapter.features?.fallback || g.key !== "fallback" || stage === "fallback").map((g, i) => {
          const here = g.key === stage;
          const m = actionMeta(STAGE_FLOW[g.key]);
          return (
            <li
              key={g.key}
              aria-current={here ? "step" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.625rem",
                padding: "0.5rem 0.625rem",
                borderRadius: "0.5rem",
                border: `1px solid ${here ? m.bd : "transparent"}`,
                background: here ? m.bg : "transparent",
                fontSize: "0.8125rem",
                color: here ? m.fg : AN_COLORS.muted,
                fontWeight: here ? 600 : 400,
              }}
            >
              <span style={{ fontFamily: AN_MONO, width: "1rem" }}>{i + 1}</span>
              <span style={{ flex: 1 }}>{g.label}</span>
              {here && <span style={{ fontSize: "0.75rem", fontWeight: 500 }}>This policy</span>}
            </li>
          );
        })}
      </ol>
      <p style={{ margin: "0.75rem 0 0", fontSize: "0.75rem", color: AN_COLORS.muted, lineHeight: 1.5 }}>
        {precedenceText(adapter)}
      </p>
      {TestSignIn && (
        <div style={{ marginTop: "0.75rem" }}>
          <TextButton onClick={() => setTestOpen(true)}>Test a sign-in</TextButton>
        </div>
      )}
    </Panel>
  );

  return (
    <PageShell>
      {messageContext}
      {modalContext}
      <div style={{ maxWidth: PAGE_MAX, width: "100%", marginInline: "auto", display: "grid", gap: "1.5rem" }}>
        <PageTitleBar
          onBack={toList}
          backLabel={`Back to ${labels.product} policies`}
          crumbs={[{ label: `${labels.product} policies`, onClick: toList }, { label: row.policyName || "Untitled policy" }]}
          title={row.policyName || "Untitled policy"}
          status={
            <>
              <ActionPill flow={draft.action} fallback={fallback} />
              <StatusDot tone={active ? "success" : "warn"}>{active ? "On" : "Paused"}</StatusDot>
            </>
          }
          actions={
            <>
              <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", fontSize: "0.875rem", color: AN_COLORS.ink2, marginRight: "0.25rem" }}>
                <Switch checked={active} onChange={(v) => setActive(row, v)} aria-label={active ? "Switch off" : "Switch on"} />
                {active ? "On" : "Off"}
              </label>
              <Button onClick={() => navigate(policiesPath(product, "new"), { state: { duplicateOf: row } })}>Duplicate</Button>
              <Button type="primary" onClick={() => navigate(`${policiesPath(product, id)}/edit`, { state: { policy: row } })}>
                Edit
              </Button>
              <RowActionMenu ariaLabel="More actions" items={[{ key: "delete", label: "Delete", danger: true, onClick: () => remove(row) }]} />
            </>
          }
        />

        <div style={{ display: "grid", gridTemplateColumns: wide ? `minmax(0, 1fr) ${ASIDE_WIDTH}` : "minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
          <div style={{ display: "grid", gap: "1rem", minWidth: 0 }}>
            {note && (
              <Note tone="info" role="status">
                {note}
              </Note>
            )}
            <Panel title="This policy will" style={{ borderTop: `3px solid ${meta.solid}` }}>
              <SummarySentence text={policySummarySentence(adapter, draft, data)} flow={draft.action} style={{ fontSize: "1.0625rem" }} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(9.5rem, 1fr))", gap: "0.75rem", marginTop: "1.25rem" }}>
                {tiles.map((t) => (
                  <Tile key={t.label} {...t} />
                ))}
              </div>
            </Panel>
            {otherRows.length > 0 && (
              <Panel title="Settings">
                <DefList rows={otherRows} />
              </Panel>
            )}
          </div>
          <aside aria-label="About this policy" style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
            {order}
            <Panel title="Details">
              <DefList rows={details} />
            </Panel>
          </aside>
        </div>
      </div>

      {TestSignIn && (
        <Drawer title="Test a sign-in" open={testOpen} onClose={() => setTestOpen(false)} width="28rem">
          <TestSignIn draft={draft} data={data} />
        </Drawer>
      )}
    </PageShell>
  );
}
